import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { FiArrowRight } from 'react-icons/fi';
import TopBar from '../components/TopBar';
import ResolvedStorageImage from '../components/ResolvedStorageImage';
import layoutStyles from '../styles/layout.module.css';
import { db } from '../firebase';
import listStyles from './ArtistList.module.css';

export default function ArtistList({ embedded = false }) {
  const navigate = useNavigate();
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const artistsQuery = query(collection(db, 'artists'), where('discoveryStatus', '==', 'listed'), orderBy('createdAt', 'desc'));
    (async () => {
      try {
        const snap = await getDocs(artistsQuery);
        if (!active) return;
        setArtists(snap.docs.map((docSnap) => {
          const data = docSnap.data() || {};
          return {
            id: docSnap.id,
            artistId: data.artistId || docSnap.id,
            name: data.artistFullName || 'Untitled Artist',
            photoPath: data.artistProfilePhotoThumbPath || data.artistProfilePhoto200Path || data.artistProfilePhotoPath || '',
          };
        }));
        setError('');
      } catch (err) {
        if (!active) return;
        setArtists([]);
        setError(err?.message || 'Failed to load artists.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const content = useMemo(() => {
    if (loading) return <div className={listStyles.stateCard}>Loading live artists…</div>;
    if (error) return <div className={listStyles.errorCard}>{error}</div>;
    if (!artists.length) return <div className={listStyles.stateCard}>No artists are listed yet. As soon as a public artist profile goes live, it will appear here.</div>;
    return (
      <div className={listStyles.grid}>
        {artists.map((artist, index) => (
          <button
            key={artist.id}
            type="button"
            onClick={() => navigate(`/artists/${artist.artistId}`)}
            className={listStyles.card}
            style={{ animationDelay: `${index * 40}ms` }}
          >
            <div className={listStyles.media}>
              <ResolvedStorageImage path={artist.photoPath} alt={artist.name} fallback={<div className={listStyles.placeholder}>Artist</div>} />
            </div>
            <div className={listStyles.cardBody}>
              <span className={listStyles.artistEyebrow}>Artist</span>
              <h2>{artist.name}</h2>
            </div>
            <FiArrowRight className={listStyles.cardArrow} />
          </button>
        ))}
      </div>
    );
  }, [artists, error, loading, navigate]);

  if (embedded) {
    return (
      <section className={listStyles.section}>
        <header className={listStyles.header}>
          <div>
            <h1>Featured Artists</h1>
            <p>Early Bird Release: Razzberry is opening this special event with a curated group of featured artists, live collectibles, and public artist pages.</p>
          </div>
        </header>
        {content}
      </section>
    );
  }

  return (
    <div className={layoutStyles.detailPage}>
      <TopBar variant="back" backLabel="Back" onBack={() => navigate('/home')} rightActionLabel="Home" onRightAction={() => navigate('/home')} />
      <div className={layoutStyles.detailMain}>
        <section className={listStyles.standaloneSection}>
          {content}
        </section>
      </div>
    </div>
  );
}
